import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { fi } from 'date-fns/locale';
import {
  ArrowLeft,
  Edit2,
  Trash2,
  X,
  Check,
  AlertCircle,
  Scale,
} from 'lucide-react';
import { weightApi } from '@/lib/api';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import type { WeightEntry } from '@/types';

function EditEntryModal({
  entry,
  onClose,
  onSaved,
}: {
  entry: WeightEntry;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [weight, setWeight] = useState(entry.weight.toString().replace('.', ','));
  const [notes, setNotes] = useState(entry.notes || '');
  const [error, setError] = useState<string | null>(null);

  const updateMutation = useMutation({
    mutationFn: (data: { weight?: number; notes?: string | null }) =>
      weightApi.update(entry.id, data),
    onSuccess: () => {
      onSaved();
      onClose();
    },
    onError: () => {
      setError('Virhe tallennettaessa muutoksia');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const weightNum = parseFloat(weight.replace(',', '.'));
    if (isNaN(weightNum) || weightNum < 20 || weightNum > 500) {
      setError('Syötä paino väliltä 20-500 kg');
      return;
    }
    setError(null);
    updateMutation.mutate({
      weight: weightNum,
      notes: notes || null,
    });
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="font-semibold text-gray-900 dark:text-white">
            Muokkaa mittausta
          </h3>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-sm"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
              Päivämäärä
            </label>
            <input
              type="text"
              value={format(new Date(entry.date), 'd.M.yyyy', { locale: fi })}
              disabled
              className="input w-full bg-gray-100 dark:bg-gray-700"
            />
            <p className="text-xs text-gray-500 mt-1">
              Päivämäärää ei voi muuttaa. Poista ja luo uusi merkintä tarvittaessa.
            </p>
          </div>

          <div>
            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
              Paino (kg)
            </label>
            <input
              type="text"
              inputMode="decimal"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              className="input w-full"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm text-gray-600 dark:text-gray-400 mb-1">
              Muistiinpano
            </label>
            <input
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Valinnainen"
              className="input w-full"
            />
          </div>

          {error && (
            <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-ghost flex-1"
            >
              Peruuta
            </button>
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="btn btn-primary flex-1"
            >
              {updateMutation.isPending ? 'Tallennetaan...' : 'Tallenna'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DeleteConfirmModal({
  entry,
  onClose,
  onDeleted,
}: {
  entry: WeightEntry;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const deleteMutation = useMutation({
    mutationFn: () => weightApi.delete(entry.id),
    onSuccess: () => {
      onDeleted();
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-sm w-full p-6">
        <h3 className="font-semibold text-gray-900 dark:text-white text-lg">
          Poista mittaus?
        </h3>
        <p className="text-gray-500 dark:text-gray-400 mt-2">
          Haluatko varmasti poistaa mittauksen{' '}
          <span className="font-medium">{entry.weight} kg</span> päivältä{' '}
          <span className="font-medium">
            {format(new Date(entry.date), 'd.M.yyyy', { locale: fi })}
          </span>
          ?
        </p>

        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            className="btn btn-ghost flex-1"
            disabled={deleteMutation.isPending}
          >
            Peruuta
          </button>
          <button
            onClick={() => deleteMutation.mutate()}
            disabled={deleteMutation.isPending}
            className="btn bg-red-600 hover:bg-red-700 text-white flex-1"
          >
            {deleteMutation.isPending ? 'Poistetaan...' : 'Poista'}
          </button>
        </div>
      </div>
    </div>
  );
}

function WeightEntryRow({
  entry,
  onEdit,
  onDelete,
}: {
  entry: WeightEntry;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const entryDate = new Date(entry.date);

  return (
    <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700 last:border-0">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
          <Scale className="h-5 w-5 text-primary-600 dark:text-primary-400" />
        </div>
        <div>
          <p className="font-medium text-gray-900 dark:text-white">
            {entry.weight} kg
          </p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {format(entryDate, 'EEEE d.M.yyyy', { locale: fi })}
          </p>
          {entry.notes && (
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
              {entry.notes}
            </p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={onEdit}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
        >
          <Edit2 className="h-4 w-4" />
        </button>
        <button
          onClick={onDelete}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg text-gray-500 hover:text-red-600"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export function WeightHistoryPage() {
  const [editingEntry, setEditingEntry] = useState<WeightEntry | null>(null);
  const [deletingEntry, setDeletingEntry] = useState<WeightEntry | null>(null);
  const queryClient = useQueryClient();

  const { data: entries, isLoading } = useQuery({
    queryKey: ['weight-entries'],
    queryFn: () => weightApi.list(),
  });

  const handleSaved = () => {
    queryClient.invalidateQueries({ queryKey: ['weight-entries'] });
    queryClient.invalidateQueries({ queryKey: ['weight-stats'] });
    queryClient.invalidateQueries({ queryKey: ['bmi'] });
  };

  // Group entries by month
  const groupedEntries = entries?.reduce((acc, entry) => {
    const monthKey = format(new Date(entry.date), 'yyyy-MM');
    if (!acc[monthKey]) {
      acc[monthKey] = [];
    }
    acc[monthKey].push(entry);
    return acc;
  }, {} as Record<string, WeightEntry[]>);

  const sortedMonths = groupedEntries
    ? Object.keys(groupedEntries).sort((a, b) => b.localeCompare(a))
    : [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/weight"
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Painohistoria
          </h1>
          {entries && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {entries.length} mittausta
            </p>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      ) : entries && entries.length > 0 ? (
        <div className="space-y-6">
          {sortedMonths.map((month) => (
            <div key={month} className="card p-4">
              <h2 className="font-medium text-gray-900 dark:text-white mb-3 capitalize">
                {format(new Date(month + '-01'), 'LLLL yyyy', { locale: fi })}
              </h2>
              <div>
                {groupedEntries![month]!.map((entry) => (
                  <WeightEntryRow
                    key={entry.id}
                    entry={entry}
                    onEdit={() => setEditingEntry(entry)}
                    onDelete={() => setDeletingEntry(entry)}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="card p-8 text-center">
          <Scale className="h-16 w-16 mx-auto text-gray-300 dark:text-gray-600" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mt-4">
            Ei painomittauksia
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mt-2">
            Aloita kirjaamalla ensimmäinen mittauksesi.
          </p>
          <Link to="/weight" className="btn btn-primary mt-4">
            Kirjaa paino
          </Link>
        </div>
      )}

      {/* Edit Modal */}
      {editingEntry && (
        <EditEntryModal
          entry={editingEntry}
          onClose={() => setEditingEntry(null)}
          onSaved={handleSaved}
        />
      )}

      {/* Delete Confirm Modal */}
      {deletingEntry && (
        <DeleteConfirmModal
          entry={deletingEntry}
          onClose={() => setDeletingEntry(null)}
          onDeleted={handleSaved}
        />
      )}
    </div>
  );
}
